import React, { useState } from 'react';
import { PortalTab } from '../../types';

// ── Universal Footer — fixed tab names + Staff Login + logo fix ──

interface FooterProps {
  setActiveTab: (tab: PortalTab) => void;
  onLoginClick?: () => void;
  currentUser?: { email: string; name?: string } | null;
  onLogout?: () => void;
}

const LOGO_URL = 'https://res.cloudinary.com/uuovmrek/image/upload/e_bgremoval/uzairuelogo01';

const NAV_LINKS: { label: string; tab: PortalTab }[] = [
  { label: 'Festival Home',      tab: 'festival-home'   },
  { label: 'Communities',        tab: 'communities'     },
  { label: 'Festival Programme', tab: 'programme'       },
  { label: 'Dignitaries',        tab: 'dignitaries'     },
  { label: 'Donate & Support',   tab: 'donate'          },
  { label: 'Vendor Registration',tab: 'vendors'         },
  { label: 'Raffle Draw',        tab: 'raffle'          },
  { label: 'Live Stream',        tab: 'live-stream'     },
  { label: 'Contact',            tab: 'contact'         },
];

const SOCIAL = [
  { label: 'YouTube',    href: 'https://www.youtube.com/@ERUEFestivalofUzairueKingdom', icon: '▶️'  },
  { label: 'Instagram',  href: 'https://www.instagram.com/erueuzairue',                  icon: '📸' },
  { label: 'Facebook',   href: 'https://www.facebook.com/profile.php?id=61595036454360', icon: '👍' },
  { label: 'X / Twitter',href: 'https://x.com/erueuzairue',                              icon: '🐦' },
  { label: 'WhatsApp',   href: 'https://wa.me/2347057840320',                             icon: '💬' },
];

export const Footer: React.FC<FooterProps> = ({
  setActiveTab, onLoginClick, currentUser, onLogout
}) => {
  const [logoErr, setLogoErr] = useState(false);

  const navBtn = (label: string, tab: PortalTab) => (
    <li key={tab}>
      <button
        onClick={() => setActiveTab(tab)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, color: '#EDE8DCCC', textAlign: 'left', padding: 0,
          transition: 'color 0.2s', fontFamily: 'Plus Jakarta Sans, sans-serif',
        }}
        onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#C9A84C'}
        onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = '#EDE8DCCC'}
      >
        {label}
      </button>
    </li>
  );

  return (
    <footer style={{
      background: '#070D0A',
      borderTop: '1px solid #1C2E22',
      padding: 'clamp(40px,6vw,64px) 24px 32px',
      fontFamily: 'Plus Jakarta Sans, sans-serif',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* ── 3-column grid ─────────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 40, marginBottom: 48,
        }}>

          {/* Col 1 — Brand + logo */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              {/* Dark circle wrapper ensures mix-blend-mode:screen works */}
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: '#070D0A',
                border: '1px solid rgba(201,168,76,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, overflow: 'hidden',
              }}>
                {!logoErr ? (
                  <img
                    src={LOGO_URL}
                    alt="ERUE Festival Logo"
                    style={{
                      width: 44, height: 44, objectFit: 'contain',
                      mixBlendMode: 'screen',
                      display: 'block',
                    }}
                    onError={() => setLogoErr(true)}
                  />
                ) : (
                  <span style={{ fontSize: 22 }}>🏛️</span>
                )}
              </div>
              <div>
                <p style={{
                  fontFamily: 'Cinzel, serif', fontSize: 15,
                  color: '#EDE8DC', fontWeight: 600, lineHeight: 1.2,
                }}>ERUE Festival</p>
                <p style={{ fontSize: 11, color: '#C9A84C', letterSpacing: 1 }}>
                  Uzairue Kingdom
                </p>
              </div>
            </div>
            <p style={{ fontSize: 13, color: '#EDE8DC80', lineHeight: 1.7, marginBottom: 14 }}>
              The annual celebration of culture, heritage and unity of
              Uzairue Kingdom, Etsako West LGA, Edo State, Nigeria.
            </p>
            <p style={{
              fontFamily: 'Cinzel, serif', fontSize: 12,
              color: '#C9A84C99', fontStyle: 'italic',
            }}>
              "One People, One Kingdom, One Destiny"
            </p>
          </div>

          {/* Col 2 — Quick Links */}
          <div>
            <h4 style={{
              fontFamily: 'Cinzel, serif', fontSize: 13,
              color: '#C9A84C', marginBottom: 16, letterSpacing: 1,
            }}>Festival Portal</h4>
            <ul style={{
              listStyle: 'none', padding: 0, margin: 0,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {NAV_LINKS.map(link => navBtn(link.label, link.tab))}
            </ul>
          </div>

          {/* Col 3 — Secretariat + Social + Login */}
          <div>
            <h4 style={{
              fontFamily: 'Cinzel, serif', fontSize: 13,
              color: '#C9A84C', marginBottom: 16, letterSpacing: 1,
            }}>Secretariat</h4>
            <ul style={{
              listStyle: 'none', padding: 0, margin: 0,
              display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24,
            }}>
              <li style={{ fontSize: 13, color: '#EDE8DCCC' }}>
                📍 Jattu-Uzairue, Etsako West LGA, Edo State
              </li>
              <li>
                <a href="tel:+2347057840320"
                   style={{ fontSize: 13, color: '#EDE8DCCC', textDecoration: 'none' }}>
                  📞 +234 705 7840 320
                </a>
              </li>
              <li>
                <a href="https://wa.me/2347057840320"
                   target="_blank" rel="noopener noreferrer"
                   style={{ fontSize: 13, color: '#25D366', textDecoration: 'none' }}>
                  💬 WhatsApp: +234 705 7840 320
                </a>
              </li>
              <li>
                <a href="mailto:erueuzairue@gmail.com"
                   style={{ fontSize: 13, color: '#EDE8DCCC', textDecoration: 'none' }}>
                  ✉️ erueuzairue@gmail.com
                </a>
              </li>
              <li>
                <a href="https://erueuzairue.org"
                   style={{ fontSize: 13, color: '#EDE8DCCC', textDecoration: 'none' }}>
                  🌐 erueuzairue.org
                </a>
              </li>
            </ul>

            <h4 style={{
              fontFamily: 'Cinzel, serif', fontSize: 13,
              color: '#C9A84C', marginBottom: 12, letterSpacing: 1,
            }}>Follow Us</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {SOCIAL.map(s => (
                <a key={s.label} href={s.href}
                   target="_blank" rel="noopener noreferrer"
                   title={s.label}
                   style={{
                     display: 'inline-flex', alignItems: 'center', gap: 6,
                     padding: '6px 12px', background: '#0E1812',
                     border: '1px solid #1C2E22', borderRadius: 6,
                     fontSize: 12, color: '#EDE8DCCC', textDecoration: 'none',
                     transition: 'border-color 0.2s, color 0.2s',
                   }}
                   onMouseEnter={e => {
                     (e.currentTarget as HTMLElement).style.borderColor = '#C9A84C';
                     (e.currentTarget as HTMLElement).style.color = '#C9A84C';
                   }}
                   onMouseLeave={e => {
                     (e.currentTarget as HTMLElement).style.borderColor = '#1C2E22';
                     (e.currentTarget as HTMLElement).style.color = '#EDE8DCCC';
                   }}
                >
                  <span>{s.icon}</span>
                  <span>{s.label}</span>
                </a>
              ))}
            </div>

            {/* ── Staff Login / Logout ───────────────────────── */}
            <div style={{
              borderTop: '1px solid #1C2E22', paddingTop: 16, marginTop: 4,
            }}>
              {currentUser ? (
                <div style={{ display: 'flex', alignItems: 'center',
                              justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: '#C9A84C' }}>
                    🔐 {currentUser.name || currentUser.email}
                  </span>
                  {onLogout && (
                    <button onClick={onLogout}
                      style={{
                        background: 'none', border: '1px solid #1C2E22',
                        borderRadius: 6, cursor: 'pointer', fontSize: 11,
                        color: '#EDE8DC60', padding: '4px 10px',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        transition: 'color 0.2s, border-color 0.2s',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.color = '#F87171';
                        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(248,113,113,0.4)';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.color = '#EDE8DC60';
                        (e.currentTarget as HTMLElement).style.borderColor = '#1C2E22';
                      }}
                    >
                      Sign Out
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={onLoginClick}
                  style={{
                    background: 'rgba(201,168,76,0.08)',
                    border: '1px solid rgba(201,168,76,0.25)',
                    borderRadius: 8, cursor: 'pointer',
                    fontSize: 13, color: '#C9A84C',
                    padding: '9px 18px', width: '100%',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontWeight: 600, letterSpacing: 0.5,
                    transition: 'background 0.2s, border-color 0.2s',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 8,
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.15)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.5)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.08)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.25)';
                  }}
                >
                  🔐 Staff &amp; Admin Login
                </button>
              )}
            </div>
          </div>

        </div>

        {/* ── Bottom bar ──────────────────────────────────────── */}
        <div style={{
          borderTop: '1px solid #1C2E22', paddingTop: 20,
          display: 'flex', flexWrap: 'wrap',
          justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <p style={{ fontSize: 12, color: '#EDE8DC40' }}>
            © {new Date().getFullYear()} ERUE Festival of Uzairue Kingdom.
            All rights reserved.
          </p>
          <p style={{ fontSize: 12, color: '#EDE8DC30' }}>
            Under the Royal Patronage of HRH The Ogieneni of Uzairue Kingdom
          </p>
        </div>

      </div>
    </footer>
  );
};
